import type { Slice } from "@reduxjs/toolkit";
import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { WorkspaceRadioChannelUpdatedDto } from "@somewear/api";
import { IWorkspaceRadioChannelToggle } from "@somewear/radio";

import type { WorkspaceState } from "../workspace.state";
import {
	emitWorkspaceRadioChannelReceived,
	emitWorkspaceRadioChannelUpdated,
} from "./workspace.radio.actions";
import {
	addRadioChannelToggles,
	clearAllRadioChannelTogglesForWorkspace,
	clearRadioChannelToggles,
} from "./workspace.radio.queuedToggles.actions";

const adapter = createEntityAdapter<IWorkspaceRadioChannelToggle, string>({
	selectId: (toggle) => `${toggle.workspaceId}-${toggle.radioChannelId}`,
});

export const { selectAll: selectAllQueuedWorkspaceRadioChannelToggles } = adapter.getSelectors(
	(state: WorkspaceState) => state.queuedRadioChannelToggles,
);

export const queuedRadioChannelTogglesSlice: Slice = createSlice({
	name: "queuedRadioChannelToggles",
	initialState: adapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(clearRadioChannelToggles, (state, action) => {
			if (!action.payload) return;
			const idsToRemove = action.payload.map(
				(toggle) => `${toggle.workspaceId}-${toggle.radioChannelId}`,
			);
			adapter.removeMany(state, idsToRemove);
		});
		builder.addCase(addRadioChannelToggles, (state, action) => {
			adapter.upsertMany(state, action.payload);
		});
		builder.addCase(clearAllRadioChannelTogglesForWorkspace, (state, action) => {
			if (!action.payload) return;
			const togglesToRemove = Object.values(state.entities)
				.filter((toggle) => toggle.workspaceId === action.payload)
				.map((toggle) => `${toggle.workspaceId}-${toggle.radioChannelId}`);
			adapter.removeMany(state, togglesToRemove);
		});
		builder.addCase(emitWorkspaceRadioChannelReceived, (state, action) => {
			if (!action.payload.radioChannel) return;

			// This should only happen when an existing organizational channel is added to a workspace
			adapter.removeOne(
				state,
				`${action.payload.workspaceId}-${action.payload.radioChannel.id}`,
			);
		});
		builder.addCase(emitWorkspaceRadioChannelUpdated, (state, action) => {
			if (action.payload.status === WorkspaceRadioChannelUpdatedDto.Status.REMOVED) {
				adapter.removeOne(
					state,
					`${action.payload.workspaceId}-${action.payload.radioChannelId}`,
				);
			}
		});
	},
});
